import { makeStyles, Theme } from "@material-ui/core";
import EmailOutlinedIcon from "@material-ui/icons/EmailOutlined";
import LinkOutlinedIcon from "@material-ui/icons/LinkOutlined";
import React, { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { useQuery } from "../../../hooks";
import { getSignUpUrl } from "../../../utils/hubRoutes";
import { Layout } from "../../components/Layout";
import { PageHelmet } from "../../components/PageHelmet";
import { FacebookAuthButton } from "./components/FacebookAuthButton/FacebookAuthButton";
import { GoogleAuthButton } from "./components/GoogleAuthButton/GoogleAuthButton";
import { useLoginPage } from "./useLoginPage";

const useStyles = makeStyles((theme: Theme) => ({
  buttonsWrapper: {
    display: "flex",
    flexDirection: "column",
    alignSelf: "center",
    maxWidth: "100%",
    gap: 16,
    flexGrow: 0,
  },
  or: {
    marginTop: 20,
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontWeight: 700,
    fontSize: 14,
  },
  divider: {
    flex: 1,
    height: 1,
    backgroundColor: "#eaeaea",
  },
  boxBlock: {
    display: "flex",
    gap: 16,
    marginTop: 20,
    fontSize: 13,
    [theme.breakpoints.up("xs")]: {
      fontSize: 14,
    },
  },
  box: {
    border: "1px solid #c2c2c2",
    borderRadius: 4,
    flex: 1,
    height: 70,
    textAlign: "center",
    color: "#333333",
    textDecoration: "none",
    fontWeight: 600,
    fontFamily: "var(--regular-font)",
    paddingTop: 11,
    "&:hover": {
      boxShadow: "0px 1px 12px 0px rgba(0, 0, 0, 0.12)",
    },
  },
  linkBlock: {
    textAlign: "center",
    marginTop: 28,
    fontWeight: 700,
  },
  signUpLink: {
    marginLeft: 6,
    fontWeight: 600,
  },
}));

const links = [
  {
    id: "login.by.password",
    to: "login-password",
    icon: <EmailOutlinedIcon />,
  },
  {
    id: "login.by.email",
    to: "login-email",
    icon: <LinkOutlinedIcon />,
  },
];

export const LoginPage = () => {
  const { locale, onResolve } = useLoginPage();
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const query = useQuery();
  const redirect_url = query.get("redirect_url");
  const pass_token = query.get("pass_token");

  const search = useMemo(() => {
    if (redirect_url) {
      return new URLSearchParams({
        redirect_url,
        ...(pass_token ? { pass_token } : {}),
      }).toString();
    }
    return "";
  }, [redirect_url, pass_token]);

  return (
    <Layout title={formatMessage({ id: "login.welcome" })}>
      <PageHelmet path="login" />
      <div className={classes.buttonsWrapper}>
        <FacebookAuthButton onResolve={onResolve} />
        <GoogleAuthButton onResolve={onResolve} />
      </div>
      <div className={classes.or}>
        <div className={classes.divider} />
        <FormattedMessage id="login.with" />
        <div className={classes.divider} />
      </div>
      <div className={classes.boxBlock}>
        {links.map(({ id, to, icon }) => (
          <Link key={id} to={{ pathname: `/${locale}/${to}`, search }} className={classes.box}>
            {icon}
            <br />
            <FormattedMessage id={id} />
          </Link>
        ))}
      </div>
      <div className={classes.linkBlock}>
        <FormattedMessage id="login.noAccount" />
        <a href={getSignUpUrl(locale)} className={classes.signUpLink}>
          <FormattedMessage id="login.create" />
        </a>
      </div>
    </Layout>
  );
};
